import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

export default function GrowthCenter({ store }) {
  const [feedback, setFeedback] = useState(null);
  const [clienteCanje, setClienteCanje] = useState(null);
  const [puntosCanje, setPuntosCanje] = useState("");
  const [motivoCanje, setMotivoCanje] = useState("Canje en tienda");

  const [clienteOrigen, setClienteOrigen] = useState(null);
  const [nombreReferido, setNombreReferido] = useState("");
  const [telefonoReferido, setTelefonoReferido] = useState("");

  const [canalCampana, setCanalCampana] = useState("INSTAGRAM");
  const [objetivoCampana, setObjetivoCampana] = useState("TRAFICO_TIENDA");
  const [codigoCampana, setCodigoCampana] = useState("");
  const [contenidoCampana, setContenidoCampana] = useState("");
  const [descuentoTipoCampana, setDescuentoTipoCampana] = useState("NINGUNO");
  const [descuentoValorCampana, setDescuentoValorCampana] = useState("");

  const clientes = store.clientesConGrowth || [];
  const referidos = store.referidos || [];
  const campanas = store.campanasSociales || [];
  const campanasConRendimiento = store.campanasConRendimiento || [];

  const stats = useMemo(() => {
    const totalPuntos = clientes.reduce((acc, c) => acc + (c.puntos || 0), 0);
    const referidosConvertidos = referidos.filter((r) => r.estado === "CONVERTIDO").length;
    return {
      clientes: clientes.length,
      totalPuntos,
      referidos: referidos.length,
      referidosConvertidos,
      campanas: campanas.length,
    };
  }, [campanas.length, clientes, referidos]);

  const handleCanje = () => {
    if (!clienteCanje) {
      setFeedback({ tipo: "warning", msg: "Selecciona cliente para canje" });
      return;
    }
    const puntos = parseInt(puntosCanje) || 0;
    if (puntos <= 0) {
      setFeedback({ tipo: "warning", msg: "Ingresa puntos válidos" });
      return;
    }

    const result = store.registrarCanjeFidelizacion({
      cliente_id: clienteCanje.id,
      puntos,
      motivo: motivoCanje,
    });

    if (!result.ok) {
      setFeedback({ tipo: "error", msg: result.reason || "No se pudo canjear" });
      return;
    }

    setFeedback({ tipo: "success", msg: "Canje de fidelización registrado" });
    setPuntosCanje("");
  };

  const handleReferido = () => {
    if (!clienteOrigen) {
      setFeedback({ tipo: "warning", msg: "Selecciona cliente origen" });
      return;
    }
    if (!telefonoReferido.trim()) {
      setFeedback({ tipo: "warning", msg: "Teléfono de referido requerido" });
      return;
    }

    const result = store.registrarReferido({
      cliente_origen_id: clienteOrigen.id,
      nombre_referido: nombreReferido,
      telefono_referido: telefonoReferido,
    });

    if (!result.ok) {
      setFeedback({ tipo: "error", msg: result.reason || "No se pudo registrar referido" });
      return;
    }

    setFeedback({ tipo: "success", msg: "Referido registrado" });
    setNombreReferido("");
    setTelefonoReferido("");
  };

  const handleConvertirReferido = (ref) => {
    const result = store.actualizarEstadoReferido({
      referido_id: ref.id,
      estado: "CONVERTIDO",
      monto_compra: ref.monto_compra || 0,
    });

    if (!result.ok) {
      setFeedback({ tipo: "error", msg: result.reason || "No se pudo convertir" });
      return;
    }

    setFeedback({ tipo: "success", msg: "Referido convertido y bono aplicado" });
  };

  const handleCampana = () => {
    if (!codigoCampana.trim()) {
      setFeedback({ tipo: "warning", msg: "Código de campaña requerido" });
      return;
    }

    if (!contenidoCampana.trim()) {
      setFeedback({ tipo: "warning", msg: "Contenido de campaña requerido" });
      return;
    }

    const aplicaDescuento = descuentoTipoCampana !== "NINGUNO";
    const valorDescuento = parseFloat(descuentoValorCampana) || 0;
    if (aplicaDescuento && valorDescuento <= 0) {
      setFeedback({ tipo: "warning", msg: "Ingresa un descuento válido para la campaña" });
      return;
    }

    store.registrarCampanaSocial({
      canal: canalCampana,
      objetivo: objetivoCampana,
      codigo: codigoCampana,
      contenido: contenidoCampana,
      descuento: aplicaDescuento
        ? {
            tipo: descuentoTipoCampana,
            valor: valorDescuento,
          }
        : null,
    });

    setFeedback({ tipo: "success", msg: "Campaña registrada" });
    setCodigoCampana("");
    setContenidoCampana("");
    setDescuentoTipoCampana("NINGUNO");
    setDescuentoValorCampana("");
  };

  const handleToggleCampana = (campana) => {
    const estadoActual = (campana?.estado || "ACTIVA").toUpperCase();
    const siguienteEstado = estadoActual === "INACTIVA" ? "ACTIVA" : "INACTIVA";

    const result = store.actualizarEstadoCampana({
      campana_id: campana.id,
      codigo: campana.codigo,
      estado: siguienteEstado,
    });

    if (!result?.ok) {
      setFeedback({ tipo: "error", msg: result?.reason || "No se pudo actualizar el estado" });
      return;
    }

    setFeedback({
      tipo: "success",
      msg: `Campaña ${campana.codigo || "SIN-COD"} ${siguienteEstado === "INACTIVA" ? "inactivada" : "activada"}`,
    });
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={2}>
        Growth y Fidelización
      </Typography>

      {feedback && (
        <Alert severity={feedback.tipo} sx={{ mb: 2 }}>
          {feedback.msg}
        </Alert>
      )}

      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} md={2}>
          <Card variant="outlined"><CardContent><Typography color="text.secondary">Clientes</Typography><Typography variant="h5" fontWeight={800}>{stats.clientes}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card variant="outlined"><CardContent><Typography color="text.secondary">Puntos vivos</Typography><Typography variant="h5" fontWeight={800}>{stats.totalPuntos}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card variant="outlined"><CardContent><Typography color="text.secondary">Referidos</Typography><Typography variant="h5" fontWeight={800}>{stats.referidos}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card variant="outlined"><CardContent><Typography color="text.secondary">Convertidos</Typography><Typography variant="h5" fontWeight={800}>{stats.referidosConvertidos}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={2}>
          <Card variant="outlined"><CardContent><Typography color="text.secondary">Campañas</Typography><Typography variant="h5" fontWeight={800}>{stats.campanas}</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography fontWeight={700} mb={1}>Canje de puntos</Typography>
              <Stack spacing={1.5}>
                <Autocomplete
                  options={clientes}
                  value={clienteCanje}
                  onChange={(_, v) => setClienteCanje(v)}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  getOptionLabel={(c) => `${c.nombre} (${c.puntos || 0} pts)`}
                  renderInput={(params) => <TextField {...params} label="Cliente" size="small" />}
                />
                <TextField
                  label="Puntos a canjear"
                  value={puntosCanje}
                  onChange={(e) => setPuntosCanje(e.target.value)}
                  size="small"
                  type="number"
                />
                <TextField
                  label="Motivo"
                  value={motivoCanje}
                  onChange={(e) => setMotivoCanje(e.target.value)}
                  size="small"
                />
                <Button variant="contained" onClick={handleCanje}>Registrar canje</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography fontWeight={700} mb={1}>Referidos</Typography>
              <Stack spacing={1.5}>
                <Autocomplete
                  options={clientes}
                  value={clienteOrigen}
                  onChange={(_, v) => setClienteOrigen(v)}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  getOptionLabel={(c) => `${c.nombre} (${c.whatsapp})`}
                  renderInput={(params) => <TextField {...params} label="Cliente origen" size="small" />}
                />
                <TextField
                  label="Nombre referido"
                  value={nombreReferido}
                  onChange={(e) => setNombreReferido(e.target.value)}
                  size="small"
                />
                <TextField
                  label="Teléfono referido"
                  value={telefonoReferido}
                  onChange={(e) => setTelefonoReferido(e.target.value)}
                  size="small"
                />
                <Button variant="contained" onClick={handleReferido}>Registrar referido</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography fontWeight={700} mb={1}>Campaña social</Typography>
              <Stack spacing={1.5}>
                <Select value={canalCampana} onChange={(e) => setCanalCampana(e.target.value)} size="small">
                  <MenuItem value="INSTAGRAM">Instagram</MenuItem>
                  <MenuItem value="TIKTOK">TikTok</MenuItem>
                  <MenuItem value="FACEBOOK">Facebook</MenuItem>
                  <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                </Select>
                <Select value={objetivoCampana} onChange={(e) => setObjetivoCampana(e.target.value)} size="small">
                  <MenuItem value="TRAFICO_TIENDA">Tráfico tienda</MenuItem>
                  <MenuItem value="RECOMPRA">Recompra</MenuItem>
                  <MenuItem value="REFERIDOS">Referidos</MenuItem>
                </Select>
                <TextField
                  label="Código campaña"
                  value={codigoCampana}
                  onChange={(e) => setCodigoCampana(e.target.value)}
                  size="small"
                />
                <TextField
                  label="Contenido"
                  value={contenidoCampana}
                  onChange={(e) => setContenidoCampana(e.target.value)}
                  size="small"
                  multiline
                  minRows={2}
                />
                <Select
                  value={descuentoTipoCampana}
                  onChange={(e) => setDescuentoTipoCampana(e.target.value)}
                  size="small"
                >
                  <MenuItem value="NINGUNO">Sin descuento</MenuItem>
                  <MenuItem value="PORCENTAJE">Descuento %</MenuItem>
                  <MenuItem value="MONTO">Descuento fijo</MenuItem>
                </Select>
                {descuentoTipoCampana !== "NINGUNO" && (
                  <TextField
                    label={descuentoTipoCampana === "PORCENTAJE" ? "Descuento %" : "Descuento fijo"}
                    value={descuentoValorCampana}
                    onChange={(e) => setDescuentoValorCampana(e.target.value)}
                    size="small"
                    type="number"
                  />
                )}
                <Button variant="contained" onClick={handleCampana}>Registrar campaña</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={700} mb={1}>Tabla de referidos</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                      <TableCell>Referido</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referidos.length === 0 ? (
                      <TableRow><TableCell colSpan={3} align="center">Sin referidos</TableCell></TableRow>
                    ) : (
                      referidos.slice(0, 20).map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>
                            <Typography fontWeight={700}>{r.nombre_referido}</Typography>
                            <Typography variant="caption" color="text.secondary">{r.telefono_referido}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={r.estado} color={r.estado === "CONVERTIDO" ? "success" : "default"} />
                          </TableCell>
                          <TableCell>
                            {r.estado !== "CONVERTIDO" && (
                              <Button size="small" variant="outlined" onClick={() => handleConvertirReferido(r)}>
                                Convertir
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={700} mb={1}>Clientes con más puntos</Typography>
              <Stack spacing={1}>
                {clientes
                  .slice()
                  .sort((a, b) => (b.puntos || 0) - (a.puntos || 0))
                  .slice(0, 10)
                  .map((c) => (
                    <Paper key={c.id} variant="outlined" sx={{ p: 1.25 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography fontWeight={700}>{c.nombre}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.stats.segmento} · LTV {store.config.moneda} {(c.stats.montoAcumulado || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Chip label={`${c.puntos || 0} pts`} color="primary" variant="outlined" />
                      </Stack>
                    </Paper>
                  ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={700} mb={1}>Rendimiento de campañas</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                      <TableCell>Código</TableCell>
                      <TableCell>Canal</TableCell>
                      <TableCell>Objetivo</TableCell>
                      <TableCell align="right">Ventas</TableCell>
                      <TableCell align="right">Ingresos</TableCell>
                      <TableCell align="right">Margen</TableCell>
                      <TableCell align="right">Ticket</TableCell>
                      <TableCell align="right">Desc. Cupón</TableCell>
                      <TableCell align="right">Desc. Aplicado</TableCell>
                      <TableCell align="center">Última venta</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campanasConRendimiento.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center">Sin campañas registradas</TableCell>
                      </TableRow>
                    ) : (
                      campanasConRendimiento.slice(0, 30).map((c) => (
                        <TableRow key={c.id} hover>
                          <TableCell>
                            <Typography fontWeight={700}>{c.codigo || "SIN-COD"}</Typography>
                          </TableCell>
                          <TableCell>{c.canal || "-"}</TableCell>
                          <TableCell>{c.objetivo || "-"}</TableCell>
                          <TableCell align="right">{c.ventas}</TableCell>
                          <TableCell align="right">
                            {store.config.moneda} {(c.ingresos || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              fontWeight={700}
                              color={(c.margen || 0) >= 0 ? "success.main" : "error.main"}
                            >
                              {store.config.moneda} {(c.margen || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {store.config.moneda} {(c.ticketPromedio || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            {c?.descuento?.tipo === "PORCENTAJE"
                              ? `${(parseFloat(c.descuento.valor) || 0).toFixed(2)}%`
                              : c?.descuento?.tipo === "MONTO"
                              ? `${store.config.moneda} ${(parseFloat(c.descuento.valor) || 0).toFixed(2)}`
                              : "-"}
                          </TableCell>
                          <TableCell align="right">
                            {store.config.moneda} {(c.descuentoVentas || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            {c.ultimaVenta
                              ? new Date(c.ultimaVenta).toLocaleDateString("es")
                              : "-"}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={c.estado || "ACTIVA"}
                              color={
                                c.estado === "NO_REGISTRADA"
                                  ? "warning"
                                  : c.estado === "INACTIVA"
                                  ? "default"
                                  : "success"
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {c.estado !== "NO_REGISTRADA" ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color={c.estado === "INACTIVA" ? "success" : "warning"}
                                onClick={() => handleToggleCampana(c)}
                              >
                                {c.estado === "INACTIVA" ? "Activar" : "Inactivar"}
                              </Button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
